import { useState } from 'react';
import {
  Anchor,
  Checkbox,
  Collapse,
  Group,
  NumberInput,
  Paper,
  Stack,
  Switch,
  Table,
  Text,
  Title,
} from '@mantine/core';
import type {
  MonumentTerms,
  ScenarioPatch,
  ScenarioResult,
} from 'src/secret-menu/billing/hooks/useRevenue';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { setAssumption, setFirmOverride } from './scenario';
import { formatUnits, MoneyInput } from './ui';

type Props = {
  patch: ScenarioPatch;
  /** The scenario as priced, for what each control shows. */
  result: ScenarioResult;
  /** Today's figures, which a control returns to when set back. */
  currentState: ScenarioResult;
  /** What Monument's controls return to: today's, or full adoption's while that is switched on. */
  monumentBaseline: ScenarioResult['monument'];
  /** Firms the back end applied a change to, so the count matches the figures. */
  firmsChanged: number;
  terms: MonumentTerms;
  pipelineFirms: number;
  onChange: (patch: ScenarioPatch) => void;
};

const toPounds = (pence: number) => pence / 100;
const toPence = (value: number | string) => Math.round(Number(value || 0) * 100);
const toCount = (value: number | string) => Math.round(Number(value || 0));

/** One revenue line: a switch, and the amount it adds beside it when there is one to set. */
function IncludeRow({
  label,
  checked,
  disabled,
  onToggle,
  children,
}: {
  label: string;
  checked: boolean;
  disabled?: boolean;
  onToggle: (checked: boolean) => void;
  children?: React.ReactNode;
}) {
  return (
    <Group justify="space-between" wrap="nowrap" gap="sm" mih={36}>
      <Switch
        size="sm"
        label={label}
        checked={checked}
        disabled={disabled}
        onChange={event => onToggle(event.currentTarget.checked)}
      />
      {children && (
        <div style={{ width: 150, opacity: checked && !disabled ? 1 : 0.4 }}>{children}</div>
      )}
    </Group>
  );
}

function FirmOverrides({
  patch,
  firms,
  priced,
  onChange,
}: {
  patch: ScenarioPatch;
  firms: ScenarioResult['firms'];
  /** Each firm's line as the scenario priced it, so an empty box shows the figure it keeps. */
  priced: ScenarioResult['firms'];
  onChange: (patch: ScenarioPatch) => void;
}) {
  const pricedLine = new Map(priced.map(line => [line.companyId, line]));
  return (
    <Table verticalSpacing={4} withRowBorders={false}>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Firm</Table.Th>
          <Table.Th>Families</Table.Th>
          <Table.Th>£ a family</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {firms.map(today => {
          const firm = pricedLine.get(today.companyId) ?? today;
          const override = patch.firmOverrides?.[firm.companyId] ?? {};
          const fixed = firm.breakdown === 'ADVISER_FIXED';
          return (
            <Table.Tr key={firm.companyId}>
              <Table.Td>
                <Checkbox
                  size="xs"
                  label={firm.name}
                  checked={!override.excluded}
                  onChange={event =>
                    onChange(
                      setFirmOverride(patch, firm.companyId, {
                        excluded: !event.currentTarget.checked,
                      }),
                    )
                  }
                />
              </Table.Td>
              <Table.Td>
                <NumberInput
                  size="xs"
                  w={90}
                  min={0}
                  allowDecimal={false}
                  placeholder={firm.units === null ? '' : formatUnits(firm.units)}
                  value={override.units ?? ''}
                  disabled={override.excluded || fixed}
                  onChange={value =>
                    onChange(
                      setFirmOverride(patch, firm.companyId, {
                        units: value === '' ? undefined : toCount(value),
                      }),
                    )
                  }
                  aria-label={`${firm.name} families`}
                />
              </Table.Td>
              <Table.Td>
                <NumberInput
                  size="xs"
                  w={80}
                  min={0}
                  decimalScale={2}
                  placeholder={
                    fixed || firm.unitPricePence === null
                      ? 'Fixed'
                      : toPounds(firm.unitPricePence).toFixed(2)
                  }
                  value={
                    override.unitPricePence === undefined ? '' : toPounds(override.unitPricePence)
                  }
                  disabled={override.excluded}
                  onChange={value =>
                    onChange(
                      setFirmOverride(patch, firm.companyId, {
                        unitPricePence: value === '' ? undefined : toPence(value),
                      }),
                    )
                  }
                  aria-label={`${firm.name} price a family`}
                />
              </Table.Td>
            </Table.Tr>
          );
        })}
      </Table.Tbody>
    </Table>
  );
}

export default function ModellerControls({
  patch,
  result,
  currentState,
  monumentBaseline,
  firmsChanged,
  terms,
  pipelineFirms,
  onChange,
}: Props) {
  const [showMore, setShowMore] = useState(false);
  const baseline = monumentBaseline;
  // Each control shows your own entry as soon as you make it, not after the back end replies.
  const monument = {
    buildFeeOn: patch.monumentBuildFeeOn ?? baseline.buildFeeOn,
    buildFeePence: patch.monumentBuildFeePence ?? baseline.buildFeePence,
    licenseFeePence: patch.monumentLicenseFeePence ?? baseline.licenseFeePence,
    customers: patch.monumentCustomerCount ?? baseline.customers,
  };
  const includeMonument = patch.includeMonument ?? true;
  const changedFirms = firmsChanged;
  const firms = currentState.firms.filter(firm => !firm.isPipeline);

  return (
    <Paper p="md" withBorder radius="md">
      <Stack gap="md">
        <Title order={5}>Assumptions</Title>

        <Stack gap="xs">
          <Switch
            label="Adviser firms at full adoption"
            description="Every stated family at the standard price, never below today's bill"
            checked={patch.adviserFullAdoption ?? false}
            onChange={event =>
              onChange(
                setAssumption(patch, 'adviserFullAdoption', event.currentTarget.checked, false),
              )
            }
          />
          <Switch
            label="Strategic partner at full adoption"
            description={`The build fee and ${formatUnits(terms.fullAdoptionCustomerCount)} customers licensed, as set from the Strategic partner button`}
            checked={patch.monumentFullAdoption ?? false}
            disabled={!includeMonument}
            onChange={event =>
              onChange(
                setAssumption(patch, 'monumentFullAdoption', event.currentTarget.checked, false),
              )
            }
          />
        </Stack>

        <Stack gap={2}>
          <Group justify="space-between">
            <Text size="textSm" fw={FONT_WEIGHTS.semibold}>
              Strategic partner
            </Text>
            <Switch
              size="xs"
              label="Include strategic partner revenue"
              labelPosition="left"
              checked={includeMonument}
              onChange={event =>
                onChange(setAssumption(patch, 'includeMonument', event.currentTarget.checked, true))
              }
            />
          </Group>
          <IncludeRow
            label="Build fee"
            checked={monument.buildFeeOn}
            disabled={!includeMonument}
            onToggle={on =>
              onChange(setAssumption(patch, 'monumentBuildFeeOn', on, baseline.buildFeeOn))
            }
          >
            <MoneyInput
              size="xs"
              aria-label="Strategic partner build fee a month"
              pence={monument.buildFeePence}
              onChange={pence =>
                onChange(
                  setAssumption(patch, 'monumentBuildFeePence', pence, baseline.buildFeePence),
                )
              }
            />
          </IncludeRow>
          <Group justify="space-between" wrap="nowrap" gap="sm" mih={36}>
            <Text size="textSm">License fee, from the first customer</Text>
            <div style={{ width: 150 }}>
              <MoneyInput
                size="xs"
                aria-label="Strategic partner license fee a month"
                pence={monument.licenseFeePence}
                onChange={pence =>
                  onChange(
                    setAssumption(
                      patch,
                      'monumentLicenseFeePence',
                      pence,
                      baseline.licenseFeePence,
                    ),
                  )
                }
              />
            </div>
          </Group>
          <Group justify="space-between" wrap="nowrap" gap="sm" mih={36}>
            <Text size="textSm">Customers</Text>
            <NumberInput
              size="xs"
              w={150}
              min={0}
              allowDecimal={false}
              thousandSeparator=","
              disabled={!includeMonument}
              value={monument.customers}
              onChange={value =>
                onChange(
                  setAssumption(patch, 'monumentCustomerCount', toCount(value), baseline.customers),
                )
              }
              aria-label="Number of strategic partner customers"
            />
          </Group>
        </Stack>

        <Stack gap={2}>
          <Text size="textSm" fw={FONT_WEIGHTS.semibold}>
            Pipeline
          </Text>
          <IncludeRow
            label={`Free-trial firms convert (${pipelineFirms})`}
            checked={patch.includePipeline ?? false}
            onToggle={on => onChange(setAssumption(patch, 'includePipeline', on, false))}
          />
        </Stack>

        <Anchor component="button" size="sm" ta="left" onClick={() => setShowMore(shown => !shown)}>
          {showMore
            ? 'Fewer options'
            : `More options${changedFirms > 0 ? ` (${changedFirms} ${changedFirms === 1 ? 'firm' : 'firms'} changed)` : ''}`}
        </Anchor>
        <Collapse expanded={showMore}>
          <Stack gap="md">
            <NumberInput
              size="xs"
              label="Standard price a family"
              description="Used at full adoption and by firms on exactly the standard price. Discounts and fixed fees stay as contracted."
              prefix="£"
              min={0}
              decimalScale={2}
              value={toPounds(patch.standardUnitPricePence ?? currentState.standardUnitPricePence)}
              onChange={value =>
                onChange(
                  setAssumption(
                    patch,
                    'standardUnitPricePence',
                    toPence(value),
                    currentState.standardUnitPricePence,
                  ),
                )
              }
            />
            <Stack gap={4}>
              <Text size="textXs" fw={FONT_WEIGHTS.semibold}>
                Change a firm
              </Text>
              <Text size="textXs" c="dimmed">
                Leave a box empty to keep the firm&apos;s own figure.
              </Text>
              <FirmOverrides
                patch={patch}
                firms={firms}
                priced={result.firms}
                onChange={onChange}
              />
            </Stack>
          </Stack>
        </Collapse>
      </Stack>
    </Paper>
  );
}
